import { getDefaultUser } from "@/api/auth";
import {
    signOut as cognitoSignOut,
    fetchAuthSession,
    signIn,
    signInWithRedirect
} from "@aws-amplify/auth";
import { Base64 } from "js-base64";
import { DateTime } from "luxon";

import {
    IS_ENABLED_ENCRYPT_SIGN_IN,
    IS_ENABLED_FREE_AUTH,
    IS_ENABLED_GOOGLE_AUTH
} from "@/common/constants";
import { resetAnalytics } from "@/common/lib/analytics";
import EncryptAdapter from "@/common/lib/encrypt";
import { handleErrorAxios, parseErrorAxios } from "@/common/lib/formatter";
import Http from "@/common/lib/http";

const _signInPasswordCognito = async ({ email, password }) => {
    try {
        await signIn({ username: email, password });
        return { callback: "/callback", tenantId: null };
    } catch (error) {
        throw {
            message: error.message.includes("User does not exist")
                ? "Usuario no existe"
                : "Revisa los datos que ingresaste"
        };
    }
};

const _signInPasswordInternal = async ({
    commit,
    dispatch,
    email,
    password
}) => {
    try {
        const info = `Basic ${Base64.encode(email + ":" + password)}`;
        let response = {};
        if (IS_ENABLED_ENCRYPT_SIGN_IN) {
            const encryptedData = EncryptAdapter.instance.parseString(info);
            response = await Http.instance.post("/bo/auth/security", {
                data: encryptedData
            });
        } else {
            response = await Http.instance.post(
                "/bo/auth/sign-in",
                {},
                {
                    headers: {
                        Authorization: info
                    }
                }
            );
        }
        const { data } = response;
        commit("SET_USER", {
            name: data.firstName,
            email: data.email,
            lastName: data.lastName,
            picture: "",
            sub: data.uid
        });
        commit("SET_TENANT_ID", data.tenantId);
        commit("SET_ROLES", data.roles);
        commit("SET_ACCESS_TOKEN", data.accessToken);
        commit("SET_TENANT_NAME", data.tenantName || "");
        commit("SET_TENANT_SLUG", data.tenantSlug || "");
        commit("SET_NEED_ONBOARDING_USER", !!data.needOnboardingUser);
        commit("SET_NEED_ONBOARDING_TENANT", !!data.needOnboardingTenant);
        dispatch("applyAllPermissions", { permissions: data.permissions });
        dispatch("applyFeatureFlags", data.featureFlags);
        dispatch("applySessionCore", data);
        const needOnboardingUser = data.needOnboardingUser;
        const needOnboardingTenant = data.needOnboardingTenant;

        let callback = "/app";
        if (needOnboardingTenant) {
            callback = "/onboarding/multitenant";
        } else if (needOnboardingUser) {
            callback = "/onboarding/marketplace";
        }

        return {
            callback,
            tenantId: data.tenantId,
            needOnboardingUser,
            needOnboardingTenant
        };
    } catch (errorAxios) {
        throw parseErrorAxios(errorAxios);
    }
};

const OAUTH_SESSION_TIMEOUT_MS = 8000;

// Amplify bloquea la lectura de tokens mientras hay un flujo OAuth en vuelo, y
// si algo impide completarlo la espera no termina nunca. El limite evita que la
// vista se quede cargando en silencio, sin error y sin una sola peticion.
const _fetchTokens = async () => {
    let timeoutId;
    try {
        const { tokens } = await Promise.race([
            fetchAuthSession(),
            new Promise((_resolve, reject) => {
                timeoutId = setTimeout(
                    () =>
                        reject(
                            new Error(
                                "Amplify no devolvio la sesion a tiempo. Vuelve a intentar el ingreso."
                            )
                        ),
                    OAUTH_SESSION_TIMEOUT_MS
                );
            })
        ]);
        return tokens;
    } finally {
        clearTimeout(timeoutId);
    }
};

const _redirectApp = ({ email }) => {
    console.log("Email should redirect [%s]", email);
    location.href = "/app";
};

const _getSignInMethod = (
    isEnabledFreeAuth = false,
    isEnabledGoogleAuth = false
) => {
    if (isEnabledFreeAuth) {
        return _redirectApp;
    }
    if (isEnabledGoogleAuth) {
        return _signInPasswordCognito;
    }
    return _signInPasswordInternal;
};

const signInMethod = _getSignInMethod(
    IS_ENABLED_FREE_AUTH,
    IS_ENABLED_GOOGLE_AUTH
);

const actions = {
    async signInPassword({ commit, dispatch }, { email, password }) {
        try {
            const info = await signInMethod({
                commit,
                dispatch,
                email,
                password
            });
            return info;
        } catch (errorAxios) {
            commit("SET_ERROR_AUTH", errorAxios);
            throw {
                message: errorAxios.message,
                statusCode: 400,
                icon: "error",
                title: "Error 😥😣"
            };
        }
    },
    async signInGoogle({ commit, dispatch }) {
        try {
            await signInWithRedirect({ provider: "Google" });
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_AUTH", parseError);
            throw parseError;
        }
    },
    async getAndApplyCurrentSession({ commit, dispatch }) {
        try {
            const tokens = await _fetchTokens();
            if (!tokens) {
                throw new Error("No hay una sesión activa");
            }
            const { accessToken, idToken } = tokens;
            commit("SET_ACCESS_TOKEN", accessToken.toString());
            commit("SET_ACCESS_TOKEN_EXPIRATION_DATE", accessToken.payload.exp);
            commit("SET_USER", {
                email: idToken.payload.email,
                lastName: idToken.payload.family_name,
                name: idToken.payload.name,
                picture: idToken.payload.picture,
                sub: idToken.payload.sub
            });
            const { data } = await Http.instance.get("/bo/users/profile");
            commit("SET_ROLES", data.roles);
            commit("SET_TENANT_ID", data.tenantId);
            commit("SET_TENANT_NAME", data.tenantName || "");
            commit("SET_TENANT_SLUG", data.tenantSlug || "");
            commit("SET_NEED_ONBOARDING_USER", !!data.needOnboardingUser);
            commit("SET_NEED_ONBOARDING_TENANT", !!data.needOnboardingTenant);
            dispatch("applyAllPermissions", {
                permissions: data.permissions
            });
            dispatch("applyFeatureFlags", data.featureFlags);
            dispatch("applySessionCore", data);
            return {
                needOnboardingUser: data.needOnboardingUser,
                needOnboardingTenant: data.needOnboardingTenant,
                tenantId: data.tenantId
            };
        } catch (errorAxios) {
            console.error({ errorAxios });
            commit("SET_ACCESS_TOKEN", "");
            const parseError = handleErrorAxios(errorAxios, dispatch);
            // commit("SET_ERROR_AUTH", parseError);
            // commit("CLEAR_AUTH");
            // Auth.signOut();
            throw parseError;
        }
    },
    async getProfile({ commit, dispatch }) {
        try {
            const { data } = await Http.instance.get("/bo/users/profile", {
                params: {}
            });
            commit("SET_ROLES", data.roles);
            commit("SET_TENANT_ID", data.tenantId);
            commit("SET_TENANT_NAME", data.tenantName || "");
            commit("SET_TENANT_SLUG", data.tenantSlug || "");
            commit("SET_NEED_ONBOARDING_USER", !!data.needOnboardingUser);
            commit("SET_NEED_ONBOARDING_TENANT", !!data.needOnboardingTenant);
            dispatch("applyAllPermissions", {
                permissions: data.permissions
            });
            dispatch("applyFeatureFlags", data.featureFlags);
            dispatch("applySessionCore", data);
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_AUTH", parseError);
            throw parseError;
        }
    },
    async updateProfile(
        { commit, dispatch, state },
        {
            address,
            addressComplement,
            cityCode,
            firstName,
            identificationNumber,
            identificationType,
            lastName,
            neighborhood,
            phone
        }
    ) {
        try {
            const { data } = await Http.instance.patch("/bo/users/profile", {
                phone,
                address,
                cityCode,
                lastName,
                firstName,
                neighborhood,
                addressComplement,
                identificationType,
                identificationNumber
            });
            commit("SET_USER", {
                ...state.user,
                phone,
                address,
                cityCode,
                lastName,
                firstName,
                neighborhood,
                addressComplement,
                identificationType,
                identificationNumber
            });

            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_AUTH", parseError);
            throw parseError;
        }
    },
    async refreshAndValidateToken({ commit, dispatch, state }) {
        if (
            state.accessToken &&
            state.tokenExpirationDate <= DateTime.now().toUnixInteger()
        ) {
            console.log("Refresh token ...");
            try {
                const { tokens } = await fetchAuthSession({
                    forceRefresh: true
                });
                if (!tokens) {
                    throw new Error("No se pudo renovar la sesión");
                }
                const refreshedAccessToken = tokens.accessToken.toString();
                commit("SET_ACCESS_TOKEN", refreshedAccessToken);
                return refreshedAccessToken;
            } catch (error) {
                dispatch("signOut");
            }
            return state.accessToken;
        }
        return state.accessToken;
    },
    async applyDefaultUser({ commit, dispatch }) {
        if (!IS_ENABLED_FREE_AUTH) {
            console.log(
                "Skip apply default user only allowed in posSimpleAccess"
            );
            return;
        }
        try {
            const {
                hasAdminRole,
                operationMode,
                permissions,
                roles,
                tenantId,
                user
            } = await getDefaultUser();
            commit("SET_ROLES", roles.slice(0, 1));
            commit("SET_TENANT_ID", tenantId);
            commit("SET_HAS_ADMIN_ROLE", hasAdminRole);
            commit("SET_USER", {
                ...user,
                name: user.firstName,
                picture: "",
                sub: user.uid
            });
            commit("SET_OPERATION_MODE", operationMode);
            dispatch("applyAllPermissions", { permissions });
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_AUTH", parseError);
            throw parseError;
        }
    },
    async signOut({ commit }) {
        resetAnalytics();
        commit("CLEAR_AUTH");
        commit("control/SET_TENANT_STACK_COLORS", null, { root: true });
        commit("commons/CLEAR_COMMONS", {}, { root: true });
        if (IS_ENABLED_GOOGLE_AUTH) {
            await cognitoSignOut();
            return;
        }
        window.location.href = "/login";
    },
    applyAllPermissions({ commit, state }, { permissions }) {
        const globalPermissions = permissions
            .filter((p) => p.scope === "global")
            .map((p) => p.slug);
        const tenantPermissions = permissions
            .filter((p) => p.scope === "tenant")
            .map((p) => p.slug);
        commit("SET_GLOBAL_PERMISSIONS", globalPermissions);
        commit("SET_TENANT_PERMISSIONS", tenantPermissions);
        const activePermissions = state.tenantId
            ? tenantPermissions
            : globalPermissions;
        commit("SET_PERMISSIONS", activePermissions);
        commit("control/APPLY_SIDEBAR_ITEMS", activePermissions, {
            root: true
        });
        commit("control/APPLY_HOME_CARDS_LINKS", activePermissions, {
            root: true
        });
    },
    // billing-fase5-salida-planes: los booleanos no pasan por las mutaciones
    // del sidebar; los getters de `control` los aplican al renderizar.
    // portal-sesion-y-candados-optimizacion (design.md D1): núcleo de sesión
    // de sign-in/profile — sesión en `auth`, colores en `control`.
    applySessionCore({ commit }, data) {
        commit("SET_PENDING_LEGAL_DOCUMENTS", data.pendingLegalDocuments);
        commit("SET_SUBSCRIPTION", data.subscription);
        commit("SET_SESSION_CORE_LOADED", true);
        commit("control/SET_TENANT_STACK_COLORS", data.tenantStackColors, {
            root: true
        });
    },
    applyFeatureFlags({ commit }, featureFlags) {
        commit("SET_FEATURE_FLAGS", featureFlags || {});
    },
    applyTenantContext({ commit, state }, { tenantId, tenantSlug }) {
        commit("SET_TENANT_ID", tenantId);
        commit("SET_TENANT_SLUG", tenantSlug || "");
        commit("SET_TENANT_WAS_SELECTED", true);
        const tenantPermissions = state.tenantPermissions;
        commit("SET_PERMISSIONS", tenantPermissions);
        commit(
            "control/CLEAR_RECENT_LINK",
            {},
            {
                root: true
            }
        );
        commit("control/APPLY_SIDEBAR_ITEMS", tenantPermissions, {
            root: true
        });
        commit("control/APPLY_HOME_CARDS_LINKS", tenantPermissions, {
            root: true
        });
    },
    removeTenantContext({ commit, state }) {
        commit("SET_TENANT_ID", null);
        commit("SET_TENANT_SLUG", "");
        commit("SET_TENANT_WAS_SELECTED", false);
        commit("SET_MERCHANT_PERMISSIONS", []);
        const globalPermissions = state.globalPermissions;
        commit("SET_PERMISSIONS", globalPermissions);
        commit(
            "control/CLEAR_RECENT_LINK",
            {},
            {
                root: true
            }
        );
        commit("control/APPLY_SIDEBAR_ITEMS", globalPermissions, {
            root: true
        });
        commit("control/APPLY_HOME_CARDS_LINKS", globalPermissions, {
            root: true
        });
    },
    applyMerchantPermissions({ commit, state }, merchantPermissions = []) {
        commit("SET_MERCHANT_PERMISSIONS", merchantPermissions);
        const permissions = [
            ...new Set([...state.tenantPermissions, ...merchantPermissions])
        ];
        commit("SET_PERMISSIONS", permissions);
        commit("control/APPLY_SIDEBAR_ITEMS", permissions, {
            root: true
        });
        commit("control/APPLY_HOME_CARDS_LINKS", permissions, {
            root: true
        });
    },
    reloadMerchantPermissions({ commit, state }) {
        if (!state.tenantId) {
            return;
        }
        const tenantPermissions = state.tenantPermissions || [];
        const merchantPermissions = state.merchantPermissions || [];
        const permissions = [
            ...new Set([...tenantPermissions, ...merchantPermissions])
        ];
        commit("control/APPLY_SIDEBAR_ITEMS", permissions, {
            root: true
        });
        commit("control/APPLY_HOME_CARDS_LINKS", permissions, {
            root: true
        });
    }
};

export default actions;
